import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SystemIcon } from "@/components/SystemIcon";
import { cn } from "@/lib/utils";

/**
 * SystemCard — used on Home + Systems pages.
 * Light-theme construction-modern: bordered card on white, blue icon, hover lifts.
 * Two variants:
 *   - default: standard card
 *   - compact: tighter padding for grids
 */
export function SystemCard({ system, className = "", compact = false }) {
    return (
        <Link
            to={`/systems/${system.slug}`}
            data-testid={`system-card-${system.slug}`}
            className={cn(
                "group relative block overflow-hidden rounded-2xl border-2 border-[#0a0b0d]/8 bg-white",
                "hover:border-[#188bf6] hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(24,139,246,0.35)]",
                "transition-all duration-200",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(24,139,246,0.55)] focus-visible:ring-offset-2 focus-visible:ring-offset-white",
                className,
            )}
        >
            {/* top accent bar */}
            <span
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-1 bg-[#188bf6] opacity-0 group-hover:opacity-100 transition-opacity"
            />
            <div className={cn(compact ? "p-5" : "p-6 sm:p-7")}>
                <div className="flex items-start justify-between gap-4">
                    <span className="w-12 h-12 rounded-xl bg-[#188bf6]/8 border border-[#188bf6]/15 flex items-center justify-center group-hover:bg-[#188bf6] group-hover:border-[#188bf6] transition-colors">
                        <SystemIcon
                            slug={system.slug}
                            className="w-5 h-5 text-[#188bf6] group-hover:text-white transition-colors"
                        />
                    </span>
                    <ArrowUpRight
                        aria-hidden="true"
                        className="w-5 h-5 text-[#0a0b0d]/30 group-hover:text-[#188bf6] transition-colors"
                    />
                </div>
                <div className="mt-5 text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#4a5562]">
                    {system.order} / System
                </div>
                <h3 className="mt-1.5 text-lg sm:text-xl font-display font-extrabold tracking-tight text-[#0a0b0d]">
                    {system.name}
                </h3>
                {!compact ? (
                    <p className="mt-2.5 text-sm leading-relaxed text-[#4a5562]">
                        {system.shortDescription}
                    </p>
                ) : (
                    <p className="mt-2 text-[13px] leading-relaxed text-[#4a5562] line-clamp-2">
                        {system.tagline}
                    </p>
                )}
            </div>
        </Link>
    );
}

export default SystemCard;
